import React from "react"
import { Tag, Dropdown } from "antd"
import { EditOutlined, CopyOutlined, StopOutlined, DeleteOutlined, CheckCircleOutlined } from "@ant-design/icons"
import styles from "./equipmentTable.module.css"
import { Text } from "ui/Typography"

/**
 * Creates equipment table columns configuration
 * @param {Object} handlers - Action handlers
 * @param {Function} handlers.onEdit - Callback for edit action
 * @param {Function} handlers.onDeactivate - Callback for deactivate action
 * @param {Function} handlers.onDelete - Callback for delete action
 * @param {Function} handlers.onActivate - Callback for activate action
 * @param {Function} handlers.onDuplicate - Callback for duplicate action
 * @param {Function} handlers.onTitleClick - Callback for title click action
 * @param {Function} t - Translation function
 * @param {Object} permissions - Permission configuration
 * @param {Function} permissions.hasPermission - Permission check function
 * @param {Object} permissions.MODULES - Module constants
 * @param {Object} permissions.ACTIONS - Action constants
 * @param {string} permissions.userRole - Current user role
 * @returns {Array} Table columns configuration
 */
export const createEquipmentColumns = (handlers, t, permissions) => {
  const { onEdit, onDeactivate, onDelete, onActivate, onDuplicate, onTitleClick } = handlers
  const { hasPermission, MODULES, ACTIONS, userRole } = permissions

  return [
    {
      title: t("equipment.table.columns.equipment_title"),
      dataIndex: "title",
      key: "title",
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text, record) => (
        <button type="button" className={styles.equipmentTitleButton} onClick={() => onTitleClick?.(record)}>
          {typeof record.image === "string" ? (
            <img src={record.image} alt={text} className={styles.equipmentImage} />
          ) : (
            <div className={styles.placeholderIconContainer}>{record.image}</div>
          )}
          <Text>{text}</Text>
        </button>
      )
    },
    {
      title: t("equipment.table.columns.active_assets"),
      dataIndex: "quantity",
      key: "quantity",
      sorter: (a, b) => a.quantity - b.quantity
    },
    {
      title: t("equipment.table.columns.category"),
      dataIndex: "category",
      key: "category",
      sorter: (a, b) => a.category.localeCompare(b.category)
    },
    {
      title: t("equipment.table.columns.assigned_experiences"),
      dataIndex: "assignedExperiences",
      key: "assignedExperiences",
      sorter: (a, b) => a.assignedExperiences - b.assignedExperiences,
      render: (val) => (
        <span className={styles.assignedExperiencesCell}>
          {val} {t("equipment.table.columns.experiences_suffix")}
        </span>
      )
    },
    {
      title: t("equipment.table.columns.upcoming_bookings"),
      dataIndex: "upcomingBookings",
      key: "upcomingBookings",
      sorter: (a, b) => (a.upcomingBookings || 0) - (b.upcomingBookings || 0),
      render: (val) => `${val || 0}`
    },
    {
      title: t("equipment.table.columns.status"),
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => {
        const normalized = (status || "").toLowerCase()
        let color = "default"
        if (normalized === "active" || normalized === "published") color = "green"
        else if (normalized === "inactive" || normalized === "deactivated") color = "red"
        else if (normalized === "draft") color = "orange"
        const displayStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "—"

        return <Tag color={color}>{displayStatus}</Tag>
      }
    },
    (hasPermission?.(MODULES.EQUIPMENT, ACTIONS.EDIT) || hasPermission?.(MODULES.EQUIPMENT, ACTIONS.CREATE)) && {
      title: t("equipment.table.columns.actions"),
      key: "actions",
      render: (_, record) => {
        const isDeactivated = (record.status || "").toLowerCase() === "inactive" || (record.status || "").toLowerCase() === "deactivated"

        const menuItems = [
          hasPermission?.(MODULES.EQUIPMENT, ACTIONS.EDIT) && {
            key: "edit",
            icon: <EditOutlined />,
            label: t("equipment.table.actions.edit"),
            onClick: () => onEdit(record)
          },
          hasPermission?.(MODULES.EQUIPMENT, ACTIONS.CREATE) && {
            key: "duplicate",
            icon: <CopyOutlined />,
            label: t("equipment.table.actions.duplicate"),
            onClick: () => onDuplicate?.(record)
          },
          hasPermission?.(MODULES.EQUIPMENT, ACTIONS.EDIT) && {
            key: isDeactivated ? "activate" : "deactivate",
            icon: isDeactivated ? <CheckCircleOutlined /> : <StopOutlined />,
            label: isDeactivated ? t("equipment.table.actions.activate") : t("equipment.table.actions.deactivate"),
            onClick: () => (isDeactivated ? onActivate?.(record) : onDeactivate?.(record))
          },
          hasPermission?.(MODULES.EQUIPMENT, ACTIONS.EDIT) &&
            userRole !== "staff_user" && {
              key: "delete",
              icon: <DeleteOutlined className={styles.deleteIcon} />,
              label: t("equipment.table.actions.delete"),
              onClick: () => onDelete?.(record)
            }
        ].filter(Boolean)

        return (
          <Dropdown
            trigger={["click"]}
            menu={{
              className: styles.actionMenu,
              items: menuItems
            }}
            placement="bottomRight"
            arrow
          >
            <span className={styles.actionTrigger}>⋯</span>
          </Dropdown>
        )
      }
    }
  ].filter(Boolean)
}

export default createEquipmentColumns
